import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mic, MicOff, Loader2, AlertCircle, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// VOICE RECORDER - Composant de dictée vocale
// Utilise OpenAI Whisper via l'API backend
// =============================================

const VoiceRecorder = ({ 
  onTranscription, 
  placeholder = "Cliquez pour dicter...",
  language = "fr",
  className
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Create MediaRecorder with webm format (supported by Whisper)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        
        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else {
        setError('Impossible de démarrer l\'enregistrement.');
      }
      toast.error('Erreur d\'accès au microphone');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      // Create FormData with audio file
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      // Send to backend for transcription
      const response = await api.post('/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.text) {
        onTranscription(response.data.text);
        toast.success('Transcription terminée');
      } else {
        throw new Error('No transcription returned');
      }
      
    } catch (err) {
      console.error('Transcription error:', err);
      setError('Erreur lors de la transcription. Veuillez réessayer.');
      toast.error('Erreur de transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Recording Button */}
      <Button
        type="button"
        variant={isRecording ? "destructive" : "outline"}
        size="sm"
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isTranscribing}
        className={cn(
          "gap-2",
          isRecording && "animate-pulse"
        )}
      >
        {isTranscribing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Transcription...</span>
          </>
        ) : isRecording ? (
          <>
            <MicOff className="w-4 h-4" />
            <span>Arrêter ({formatTime(recordingTime)})</span>
          </>
        ) : (
          <>
            <Mic className="w-4 h-4" />
            <span>Dicter</span>
          </>
        )}
      </Button>

      {/* Status indicators */}
      {isRecording && (
        <Badge variant="destructive" className="gap-1 animate-pulse">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          Enregistrement
        </Badge>
      )}

      {isTranscribing && (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Traitement
        </Badge>
      )}

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
